/* ==========================================================================
   Gemians - TSA Advertorial - runtime
   Countdown timer, sticky bottom bar, lazy video facade.
   No dependencies. Safe to run more than once (theme editor re-renders).
   ========================================================================== */
(function () {
  'use strict';

  var NS = 'gemians-tsa';
  var INIT_FLAG = 'gemiansTsaInit';

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  function $all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function storage() {
    try {
      var probe = '__' + NS + '__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  var store = storage();

  /* ------------------------------------------------------------------------
     Countdown
     ------------------------------------------------------------------------ */
  function nextDailyReset(hour, minute) {
    var now = new Date();
    var target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);
    if (target.getTime() <= now.getTime()) {
      target.setDate(target.getDate() + 1);
    }
    return target.getTime();
  }

  function resolveEndTime(root, forceNew) {
    var mode = root.getAttribute('data-mode') || 'evergreen';

    if (mode === 'fixed') {
      return parseInt(root.getAttribute('data-end'), 10) || 0;
    }

    if (mode === 'daily') {
      return nextDailyReset(
        parseInt(root.getAttribute('data-reset-hour'), 10) || 0,
        parseInt(root.getAttribute('data-reset-minute'), 10) || 0
      );
    }

    // evergreen - per visitor, persisted so a reload does not restart it
    var key = root.getAttribute('data-storage-key') || NS + '-timer-end';
    var duration =
      ((parseInt(root.getAttribute('data-hours'), 10) || 0) * 3600 +
        (parseInt(root.getAttribute('data-minutes'), 10) || 0) * 60 +
        (parseInt(root.getAttribute('data-seconds'), 10) || 0)) *
      1000;

    if (!forceNew && store) {
      var stored = parseInt(store.getItem(key), 10);
      if (stored && stored > Date.now()) return stored;
    }

    var end = Date.now() + duration;
    if (store) {
      try { store.setItem(key, String(end)); } catch (e) { /* quota - ignore */ }
    }
    return end;
  }

  function initCountdown(root) {
    if (root[INIT_FLAG]) return;
    root[INIT_FLAG] = true;

    var hEl = root.querySelector('[data-gemians-tsa-timer="hours"]');
    var mEl = root.querySelector('[data-gemians-tsa-timer="minutes"]');
    var sEl = root.querySelector('[data-gemians-tsa-timer="seconds"]');
    var digits = root.querySelector('[data-gemians-tsa-timer-digits]');
    var expiredEl = root.querySelector('[data-gemians-tsa-timer-expired]');
    var onExpire = root.getAttribute('data-on-expire') || 'freeze';

    var endTime = resolveEndTime(root, false);
    var ticker = null;

    function paint(diff) {
      var totalSeconds = Math.floor(diff / 1000);
      var h = Math.floor(totalSeconds / 3600);
      var m = Math.floor((totalSeconds % 3600) / 60);
      var s = totalSeconds % 60;

      if (hEl && hEl.textContent !== pad(h)) hEl.textContent = pad(h);
      if (mEl && mEl.textContent !== pad(m)) mEl.textContent = pad(m);
      if (sEl && sEl.textContent !== pad(s)) sEl.textContent = pad(s);
    }

    function expire() {
      stop();

      if (onExpire === 'restart') {
        endTime = resolveEndTime(root, true);
        start();
        return;
      }

      if (onExpire === 'hide') {
        root.hidden = true;
        return;
      }

      paint(0);

      if (onExpire === 'message' && expiredEl) {
        if (digits) digits.hidden = true;
        expiredEl.hidden = false;
      }
    }

    function tick() {
      var diff = endTime - Date.now();
      if (diff <= 0) { expire(); return; }
      paint(diff);
    }

    function start() {
      stop();
      tick();
      ticker = window.setInterval(tick, 1000);
    }

    function stop() {
      if (ticker) { window.clearInterval(ticker); ticker = null; }
    }

    // Pause while the tab is hidden, resync immediately on return
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else if (!root.hidden) { start(); }
    });

    start();
  }

  /* ------------------------------------------------------------------------
     Sticky bottom bar
     Visible once the reader has scrolled past the trigger element, hidden
     again as soon as the stop element (footer) comes into view.
     ------------------------------------------------------------------------ */
  function initStickyBar(bar) {
    if (bar[INIT_FLAG]) return;
    bar[INIT_FLAG] = true;

    var triggerSelector = bar.getAttribute('data-trigger') || '';
    var stopSelector = bar.getAttribute('data-stop') || '';
    var threshold = parseInt(bar.getAttribute('data-threshold'), 10);
    if (isNaN(threshold)) threshold = 50;

    var trigger = triggerSelector ? document.querySelector(triggerSelector) : null;
    var stop = stopSelector ? document.querySelector(stopSelector) : null;

    if (triggerSelector && !trigger) {
      console.warn('[gemians-tsa] Sticky bar trigger "' + triggerSelector + '" matched nothing; showing the bar immediately.');
    }
    if (stopSelector && !stop) {
      console.warn('[gemians-tsa] Sticky bar stop element "' + stopSelector + '" matched nothing; the bar will not auto-hide.');
    }

    var passedTrigger = !trigger; // no trigger configured - show right away
    var reachedStop = false;

    // Observers are parked on the element. An IntersectionObserver with live
    // observations should survive GC on its own, but engines have been
    // inconsistent about that, so hold an explicit reference.
    bar.gemiansTsaObservers = [];

    function render() {
      bar.classList.toggle('is-visible', passedTrigger && !reachedStop);
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: rAF-throttled scroll, offsets cached and only recomputed on resize
      var ticking = false;
      var measure = function () {
        var viewport = window.innerHeight;
        var scrollY = window.pageYOffset;
        if (trigger) {
          passedTrigger = trigger.getBoundingClientRect().top < viewport * (threshold / 100);
        }
        if (stop) {
          reachedStop = stop.getBoundingClientRect().top <= viewport;
        }
        render();
        ticking = false;
        return scrollY;
      };
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(measure);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      measure();
      return;
    }

    if (trigger) {
      // 1px sentinel pinned to the top of the trigger element. Observed against
      // a root whose top edge is pushed down to `threshold`% of the viewport,
      // so it fires exactly twice instead of on every scroll frame.
      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
      if (getComputedStyle(trigger).position === 'static') trigger.style.position = 'relative';
      trigger.insertBefore(sentinel, trigger.firstChild);

      var triggerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.rootBounds) return;
            passedTrigger = entry.boundingClientRect.top <= entry.rootBounds.top;
            render();
          });
        },
        { rootMargin: '-' + threshold + '% 0px 0px 0px', threshold: 0 }
      );
      triggerObserver.observe(sentinel);
      bar.gemiansTsaObservers.push(triggerObserver);
    }

    if (stop) {
      var stopObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            reachedStop = entry.isIntersecting;
            render();
          });
        },
        { threshold: 0 }
      );
      stopObserver.observe(stop);
      bar.gemiansTsaObservers.push(stopObserver);
    }

    render();
  }

  /* ------------------------------------------------------------------------
     Lazy video facade
     The embed is only requested once it is about to enter the viewport, which
     keeps roughly 1 MB of player payload off the initial page load.
     ------------------------------------------------------------------------ */
  function loadEmbed(holder) {
    if (holder.getAttribute('data-loaded') === 'true') return;
    holder.setAttribute('data-loaded', 'true');

    var src = holder.getAttribute('data-src');
    if (!src) return;

    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.setAttribute('title', holder.getAttribute('data-title') || 'Video');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');

    iframe.addEventListener('load', function () {
      var poster = holder.querySelector('[data-gemians-tsa-video-poster]');
      if (poster && poster.parentNode) poster.parentNode.removeChild(poster);
    });

    holder.appendChild(iframe);
  }

  function initVideo(holder) {
    if (holder[INIT_FLAG]) return;
    holder[INIT_FLAG] = true;

    if (!('IntersectionObserver' in window)) { loadEmbed(holder); return; }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadEmbed(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(holder);
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function init(scope) {
    $all('[data-gemians-tsa-countdown]', scope).forEach(initCountdown);
    $all('[data-gemians-tsa-sticky-bar]', scope).forEach(initStickyBar);
    $all('[data-gemians-tsa-video]', scope).forEach(initVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: sections are re-rendered in place, so re-run for that subtree
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.GemiansTSA = { init: init };
})();
